import { DataType, DateTime, ParseNode } from '@mode-switch/express';
import { EncField, EncFieldGroup, EncFieldGroupType, Encoding, VariableType } from '@mode-vegas/vegas';
import { VariableLevel } from '@mode-vegas/vegas/build/esm/Guide/Encodings/Variable';
import { getBaselineFieldForSeries, makeDefaultRangeAxisTitle } from '@mode/charts/util-muze';
import { FieldFormatHelpers, SharedAxisHelpers } from '@mode/charts/util-vegas';
import { ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { ICON_FIELD_MAP, getFieldIcon } from '@mode/shared/util-js';
import { CompiledField, Switch } from '@mode/shared/util-switch';
import { upperFirst } from 'lodash';
import * as FieldHelpers from '../helpers/field-helpers';

export function makeFormatListItemFromField(
  view: FlamingoTypes.ModeView,
  field: EncField,
  encoding: Encoding,
  showLegend: boolean,
  fieldFormats?: ChartTypes.FieldFormatMap
) {
  return new EncFieldFormatListItem(view, field, encoding, showLegend, fieldFormats);
}

export function makeFormatListItemFromGroup(
  view: FlamingoTypes.ModeView,
  group: EncFieldGroup,
  encoding: Encoding,
  fieldFormats?: ChartTypes.FieldFormatMap
) {
  return new EncFieldGroupFormatListItem(view, group, encoding, fieldFormats);
}
export interface FormatListItem {
  type: 'Field' | EncFieldGroupType;
  formatKey: string;
  displayName: string;
  icon: string;
  captionText?: string;
  fieldFormula: string;
  variableType: VariableType;
  variableLevel?: VariableLevel;
  isNumeric: boolean;
  isDiscrete: boolean;
  isContinuous: boolean;
  isBinned: boolean;
  binSize: number | void;
  isLegend: boolean;
  dataType: DataType.Name | undefined;
  dateTimeUnit: DateTime.Unit | undefined;
  id: string | undefined;
  baseFormatDisplayName?: string;
  attrName?: string;
  itemKey: string;
  defaultTitle?: string;
}

function generateItemKey(field: CompiledField, formatKey: string): string {
  if (!field.isBinned) {
    return formatKey;
  }

  const expr = field.__expr;

  if (expr?.root.type === ParseNode.Type.CallExpression) {
    const callee = expr.root.callee;

    if (
      callee === 'BIN' &&
      expr.root.arguments.length === 2 &&
      expr.root.arguments[0].type === ParseNode.Type.AccessIdentifier
    ) {
      const accessName = expr.root.arguments[0].name;
      return FieldFormatHelpers.getFormatMapKey(
        `${callee}([${accessName}])`,
        Switch.Compat.makeVariableType(field.varType)
      );
    }
  }

  return formatKey;
}

export class EncFieldFormatListItem implements FormatListItem {
  type: 'Field';
  formatKey: string;
  displayName: string;
  icon: string;
  captionText?: string | undefined;
  fieldFormula: string;
  variableType: VariableType;
  variableLevel?: VariableLevel;
  isNumeric: boolean;
  isDiscrete: boolean;
  isContinuous: boolean;
  isBinned: boolean;
  binSize: number | void;
  isLegend: boolean;
  isSystem: boolean;
  dataType: DataType.Name | undefined;
  dateTimeUnit: DateTime.Unit | undefined;
  id: string | undefined;
  baseFormatDisplayName?: string;
  attrName?: string;
  itemKey: string;
  defaultTitle?: string | undefined;

  constructor(
    view: FlamingoTypes.ModeView,
    encField: EncField,
    encoding: Encoding,
    isLegend: boolean,
    fieldFormats?: ChartTypes.FieldFormatMap
  ) {
    const compiledField = CompiledField.fromEncField(view, encField);
    const formatKey = FieldFormatHelpers.getFormatMapKeyByField(encField);
    this.type = 'Field';
    this.formatKey = formatKey;
    this.fieldFormula = encField.formula;
    this.id = encField.id;
    this.isLegend = isLegend;

    const rangeBaselineField = getBaselineFieldForSeries(encoding, encField);
    if (rangeBaselineField != null) {
      const compiledBaselineField = CompiledField.fromEncField(view, rangeBaselineField);
      this.defaultTitle = makeDefaultRangeAxisTitle(compiledBaselineField, compiledField, fieldFormats);
    }

    this.displayName = compiledField.hasAnalyticFunc
      ? `${FieldHelpers.getDescriptionExprForm(compiledField, false).trimStart()} - ${compiledField.displayFormula}`
      : compiledField.displayFormula;

    this.variableType = encField.variable.type;
    this.variableLevel = encField.variable.level;
    this.isNumeric = compiledField.isFormulaNumeric;
    this.isDiscrete = compiledField.isDiscrete;
    this.isContinuous = compiledField.isContinuous;
    this.isSystem = compiledField.isSystem;
    this.isBinned = compiledField.isBinned;
    this.binSize = compiledField.binSize;
    this.dataType = compiledField.formulaDataType;
    this.dateTimeUnit = compiledField.dateFuncUnit;
    this.attrName = compiledField.attrName;

    this.itemKey = generateItemKey(compiledField, this.formatKey);

    this.icon = compiledField.hasAnalyticFunc
      ? ICON_FIELD_MAP.TABLECAL
      : getFieldIcon(compiledField.formulaDataType, this.formulaHasCalcAttr(view, encField.formula));

    this.captionText =
      compiledField.hasAnalyticFunc || compiledField.isAnalyticCalc
        ? this.buildCaptionText(encField, compiledField)
        : undefined;
    this.baseFormatDisplayName = FieldFormatHelpers.getBaseFormatDisplayName(formatKey, fieldFormats);
  }

  formulaHasCalcAttr(view: FlamingoTypes.ModeView, encFieldFormula: string): boolean {
    return !!CompiledField.formulaHasCalcAttr(view, encFieldFormula);
  }

  buildCaptionText(encField: EncField, compiled: CompiledField): string {
    const captionText = FieldHelpers.concatDescriptionFrameAndRelativeTo(encField, compiled);
    return `${upperFirst(captionText.trimStart())}`;
  }
}

export class EncFieldGroupFormatListItem implements FormatListItem {
  type: EncFieldGroupType;
  formatKey: string;
  displayName: string;
  icon: string;
  captionText?: string | undefined;
  fieldFormula: string;
  variableType: VariableType;
  attrName?: string;
  isNumeric = true;
  isDiscrete = false;
  isContinuous = true;
  isBinned = false;
  binSize = undefined;
  isLegend: boolean;
  dataType: DataType.Name | undefined;
  dateTimeUnit: DateTime.Unit | undefined;
  id: string | undefined;
  itemKey: string;
  baseFormatDisplayName?: string;

  constructor(
    view: FlamingoTypes.ModeView,
    group: EncFieldGroup,
    encoding: Encoding,
    fieldFormats?: ChartTypes.FieldFormatMap
  ) {
    this.type = group.type;
    this.id = group.id;
    this.formatKey = FieldFormatHelpers.getFormatMapKeyByGroup(group);
    this.displayName = group.name;
    this.fieldFormula = group.id;
    this.variableType = VariableType.CONTINUOUS;
    this.isLegend = false;
    this.icon = 'icon-shared-axis';
    this.itemKey = this.formatKey;

    const representitiveGroupMember = SharedAxisHelpers.getCompiledFieldRepresentingGroup(group, encoding, view);
    if (representitiveGroupMember) {
      this.isNumeric = representitiveGroupMember.isFormulaNumeric;
      this.isDiscrete = representitiveGroupMember.isDiscrete;
      this.isContinuous = representitiveGroupMember.isContinuous;
      this.dataType = representitiveGroupMember.formulaDataType;
      this.dateTimeUnit = representitiveGroupMember.dateFuncUnit;
    }
    const members = SharedAxisHelpers.getGroupMembersInEncoding(group, encoding);
    this.captionText = this.buildCaptionText(view, members);
    this.baseFormatDisplayName = FieldFormatHelpers.getBaseFormatDisplayName(this.formatKey, fieldFormats);
  }

  buildCaptionText(view: FlamingoTypes.ModeView, members: EncField[]) {
    const CHAR_LIMIT = 50;

    const memberDisplayNames = members.map((field) => {
      const compiled = CompiledField.fromEncField(view, field);
      return compiled.displayFormula;
    });
    let memberNameList = '';
    memberDisplayNames.some((name, index, source) => {
      const currentLen = memberNameList.length;
      if (currentLen === 0) {
        memberNameList = name;
        return false;
      } else if (currentLen + name.length <= CHAR_LIMIT) {
        memberNameList = `${memberNameList}, ${name}`;
        return false;
      }
      const more = source.length - index;
      memberNameList = `${memberNameList} +${more} more`;
      return true;
    });
    return `Axis shared by ${memberNameList}`;
  }
}
